import React, { useState, useEffect } from "react";
import { X, CheckCircle2 } from "lucide-react";
import { useEtatGlobal } from "../../../EtatGlobal.js";
import { httpClient } from "../../../../HttpClient";
import ArrivePCStage from "./stages/ArrivePCStage";
import TriStage from "./stages/TriStage";
import Traitement1Stage from "./stages/Traitement1Stage";
import Traitement2Stage from "./stages/Traitement2Stage";
import BouclageStage from "./stages/BouclageStage";

// tache.type -> composant d'étape. Les noms de type ("Traitement niv.2" pour
// l'endpoint /traitement1, "Traitement niv.1" pour /traitement2) reprennent
// le nommage historique du backend/getMonthTasks(), volontairement conservé
// à l'identique (cf. Traitement1Stage.js / Traitement2Stage.js).
const STAGE_COMPONENTS = {
  "Arrive PC": ArrivePCStage,
  "Tri et numérisation": TriStage,
  "Traitement niv.2": Traitement1Stage,
  "Traitement niv.1": Traitement2Stage,
  "Bouclage mensuel": BouclageStage,
};

// Orchestrateur : timeline des 5 étapes (cercles numérotés + ligne de
// connexion), toggle Consulter/Formulaire, et délégation du contenu de
// chaque étape à son propre composant sous ./stages/. Toute la logique de
// soumission (payloads, endpoints, emails, todos) vit désormais dans ces
// composants + comptaStageHelpers.js — ce fichier ne fait plus que le shell
// et la navigation entre étapes.
function OverlaySuiviCompta({
  selectedClient,
  selectedMonth,
  selectedTask,
  setSelectedTask,
  setShowOverlay,
  getMonthTasks,
  selectedYear,
  refetch,
}) {
  const { utilisateur } = useEtatGlobal();

  const [allUsers, setAllUsers] = useState([]);
  useEffect(() => {
    httpClient
      .get("https://backend-myalfa.vercel.app/api/users")
      .then((res) => {
        setAllUsers(res.data);
      })
      .catch((err) => {
        console.error("Erreur chargement utilisateurs", err);
      });
  }, []);

  if (!selectedClient || !selectedMonth) return null;

  return (
    <div
      className="fixed inset-0 bg-black/45 z-[1000] flex items-center justify-center p-4"
      onClick={() => setShowOverlay(false)}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-4 px-6 py-5 border-b border-gray-100">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 rounded-lg bg-[#5C1A30] flex items-center justify-center flex-shrink-0">
              <CheckCircle2 className="w-4 h-4 text-white" />
            </div>
            <div className="min-w-0">
              <h2 className="text-base font-bold text-gray-900 leading-tight truncate">
                Dossier : {selectedClient.nom}
              </h2>
              <p className="text-xs text-gray-400">
                {selectedMonth}/{selectedYear}
              </p>
            </div>
          </div>
          <button
            onClick={() => setShowOverlay(false)}
            className="w-8 h-8 rounded-full border border-gray-200 flex items-center justify-center text-gray-400 hover:text-gray-700 transition-colors flex-shrink-0"
          >
            <X size={16} />
          </button>
        </div>

        {/* Contenu : timeline des 5 étapes */}
        <div className="px-6 py-5 space-y-1">
          {getMonthTasks().map((tache, idx) => {
            const hasDetails =
              tache.arrivePCForm ||
              tache.triForm ||
              tache.traitement1Form ||
              tache.traitement2Form ||
              tache.bouclageForm;

            const showFormInstead =
              !tache.statut || tache.statut === false || !hasDetails;

            const isOpen = selectedTask && selectedTask.id === tache.id;
            const StageComponent = STAGE_COMPONENTS[tache.type];

            return (
              <div key={tache.id} className="flex gap-4">
                {/* Cercle numéroté + ligne de connexion */}
                <div className="flex flex-col items-center flex-shrink-0">
                  <div
                    className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold text-white flex-shrink-0 ${
                      tache.statut ? "bg-[#840040]" : "bg-gray-300"
                    }`}
                  >
                    {idx + 1}
                  </div>
                  <div
                    className={`w-0.5 flex-1 mt-1 mb-1 transition-all ${
                      selectedTask && tache.statut ? "bg-[#840040]" : "bg-gray-200"
                    }`}
                    style={{ minHeight: "16px" }}
                  />
                </div>

                {/* Infos tâche */}
                <div className="min-w-0 flex-1 pb-6">
                  <div className="flex items-center gap-2 flex-wrap">
                    <strong className="text-sm font-bold text-gray-900">{tache.type}</strong>
                    <span
                      className={`text-xs font-semibold ${
                        tache.statut ? "text-emerald-600" : "text-gray-400"
                      }`}
                    >
                      {tache.statut ? "Terminé" : "Non fait"}
                    </span>
                    <button
                      type="button"
                      onClick={() =>
                        setSelectedTask(
                          selectedTask && selectedTask.id === tache.id ? null : tache
                        )
                      }
                      className="ml-auto text-xs font-semibold text-[#840040] hover:underline"
                    >
                      {showFormInstead ? "Formulaire" : "Consulter"}
                    </button>
                  </div>

                  {/* détails ou formulaire, délégués au composant d'étape */}
                  {isOpen && (
                    <div className="mt-3">
                      {StageComponent && (
                        <StageComponent
                          tache={tache}
                          showFormInstead={showFormInstead}
                          selectedClient={selectedClient}
                          selectedYear={selectedYear}
                          selectedMonth={selectedMonth}
                          refetch={refetch}
                          setShowOverlay={setShowOverlay}
                          allUsers={allUsers}
                          utilisateur={utilisateur}
                        />
                      )}

                      {!hasDetails && !showFormInstead && (
                        <p className="text-sm text-gray-400 italic">Aucun détail disponible</p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default OverlaySuiviCompta;
